/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { Select } from "@duxton-web/ui"
import _ from "lodash"
import moment from "moment-timezone"
import { RestockSettingFreq } from "types/models/menu"

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const
export type DayType = typeof DAYS[number]

export const renderRestockLocalTime = () => {
  return _.range(24).map(time => {
    const hour = `${time < 10 ? 0 : ""}${time}`

    return [
      <Select.Option key={`${hour}:00`} value={`${hour}:00`}>
        {`${hour}:00`}
      </Select.Option>,
      <Select.Option key={`${hour}:30`} value={`${hour}:30`}>
        {`${hour}:30`}
      </Select.Option>,
    ]
  })
}

export const getNextRestockTimeFormat = ({
  locale,
  nextRestockTime,
  timezone,
}: {
  locale: string
  nextRestockTime: string
  timezone?: string
}) => {
  const time = moment
    .tz(nextRestockTime, timezone || moment.tz.guess())
    .locale(locale)

  if (!time.isValid()) {
    return null
  }

  return {
    day: time.format("dddd, MMMM D, YYYY"),
    hour: time.format("HH:mm"),
  }
}

const DAYS_ORDER: { [key in DayType]: number } = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

export const calculateNextRestockTime = ({
  frequency,
  days,
  localTime,
  timezone,
}: {
  frequency: RestockSettingFreq
  days?: DayType[]
  localTime?: string
  timezone?: string
}) => {
  const momentTz = (params?: number[]) =>
    moment.tz(params, timezone || moment.tz.guess()).locale("en")

  // Using logic from Zeus
  const now = momentTz()
  const [hour, minute] = localTime?.split(":").map(val => Number(val)) ?? [0, 0]
  const sortedDays = days?.length
    ? [...days].sort((a, b) => DAYS_ORDER[a] - DAYS_ORDER[b])
    : []

  let time = momentTz()
  let delta = -1

  switch (frequency) {
    case RestockSettingFreq.Hourly: {
      time = momentTz([
        now.year(),
        now.month(),
        now.date(),
        hour,
        minute,
        0,
      ]).add(1, "hour")

      break
    }
    case RestockSettingFreq.Daily: {
      time = momentTz([now.year(), now.month(), now.date(), hour, minute, 0])

      if (time.isBefore(momentTz())) {
        time.add(1, "day")
      }

      break
    }
    case RestockSettingFreq.Weekly: {
      for (let i = 0; i < sortedDays.length; i += 1) {
        if (DAYS_ORDER[sortedDays[i]] === now.weekday()) {
          if (
            now.hour() < hour ||
            (now.hour() === hour && minute > now.minute())
          ) {
            delta = 0
            break
          }
        }

        if (DAYS_ORDER[sortedDays[i]] > now.weekday()) {
          delta = DAYS_ORDER[sortedDays[i]] - now.weekday()
          break
        }
      }

      if (delta === -1) {
        delta = DAYS_ORDER.Sat - now.weekday() + 1

        if (sortedDays.length > 0) {
          delta += DAYS_ORDER[sortedDays[0]]
        }
      }

      time = momentTz([
        now.year(),
        now.month(),
        now.date(),
        hour,
        minute,
        0,
      ]).add(delta, "day")

      break
    }
    default:
      break
  }

  return time.toISOString()
}
